import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { FullPageLoader } from '@/components/ui/loader';
import { ApiError } from '@/lib/api-client';
import { qk } from '@/lib/query-keys';

import { AuthContext } from './auth-context';

const PUBLIC_ROUTES = new Set<string>([
  '/', '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/accept-invite',
]);

/**
 * Loads `/auth/me` once at mount and stashes the operator in context.
 * Global `mb:unauthorized` events (fired by the api-client interceptor
 * on 401) trigger a route to /login. On successful sign-out we clear
 * the whole query cache so stale per-tenant data can't leak into the
 * next session.
 */
export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: qk.auth.me,
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Even if logout POST fails, we still want the local session gone.
    }
    qc.clear();
    window.location.href = '/';
  }, [qc]);

  useEffect(() => {
    const handler = () => {
      qc.clear();
      navigate('/login', { replace: true });
    };
    window.addEventListener('mb:unauthorized', handler);
    return () => window.removeEventListener('mb:unauthorized', handler);
  }, [qc, navigate]);

  useEffect(() => {
    if (meQuery.isLoading) return;
    const path = location.pathname;
    const isPublic = PUBLIC_ROUTES.has(path) || path.startsWith('/accept-invite') || path.startsWith('/reset-password');
    const err = meQuery.error;
    const isAnon = err instanceof ApiError && err.status === 401;
    if (isAnon && !isPublic) {
      navigate('/login', { replace: true });
    }
  }, [meQuery.isLoading, meQuery.error, location.pathname, navigate]);

  if (meQuery.isLoading) return <FullPageLoader />;

  return (
    <AuthContext.Provider
      value={{
        operator: meQuery.data ?? null,
        isLoading: meQuery.isLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
